import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import {
  PERMISSION_ACTION_CODES,
  PERMISSION_RESOURCE_CODES,
} from '@/permissions/definitions';

const buildAdminAccessMap = (
  authorizedResourceAccess: AuthorizedResourceAccessMap = {},
): AuthorizedResourceAccessMap => {
  const adminAccessMap: AuthorizedResourceAccessMap = {
    ...authorizedResourceAccess,
  };

  Object.values(PERMISSION_RESOURCE_CODES).forEach((resourceCode) => {
    adminAccessMap[resourceCode] = true;
  });

  Object.entries(PERMISSION_ACTION_CODES).forEach(
    ([resourceKey, resourceActions]) => {
      const resourceCode =
        PERMISSION_RESOURCE_CODES[
          resourceKey as keyof typeof PERMISSION_RESOURCE_CODES
        ];

      if (resourceCode) {
        Object.values(resourceActions).forEach((actionCode) => {
          adminAccessMap[`${resourceCode}:${actionCode}`] = true;
        });
      }
    },
  );

  return adminAccessMap;
};

/**
 * 建立页面与模块权限映射体系
 * @see https://umijs.org/docs/max/access#access
 */
export default function access(
  initialState:
    | {
        authorizedResourceAccess?: AuthorizedResourceAccessMap;
        currentTenantIsAdmin?: boolean;
      }
    | undefined,
) {
  const { authorizedResourceAccess, currentTenantIsAdmin } = initialState ?? {};

  const baseMap: AuthorizedResourceAccessMap =
    currentTenantIsAdmin || process.env.NODE_ENV === 'development'
      ? buildAdminAccessMap(authorizedResourceAccess)
      : { ...(authorizedResourceAccess || {}) };

  // 跨作用域组合权限计算（例如站点上下文下的子模块权限）
  const hasSite = Boolean(baseMap.cmsSite || baseMap['cms-site']);
  baseMap.cmsSiteSeo = Boolean(
    hasSite && (baseMap.cmsSeo || baseMap['cms-seo']),
  );
  baseMap.cmsSiteContent = Boolean(
    hasSite && (baseMap.cmsContent || baseMap['cms-content']),
  );
  baseMap.cmsSiteLead = Boolean(
    hasSite && (baseMap.cmsLead || baseMap['cms-lead']),
  );

  // 模块级权限向二级子页面按钮的双向兼容映射
  // 如果已授予 cmsContent:create，则自动兼容 cms-content-asset:create
  const moduleAliases: Record<string, string[]> = {
    cmsContent: [
      'cms-content-asset',
      'cms-content-category',
      'cms-content-tag',
      'cms-content-review',
      'cms-content-material',
      'cms-content-editor',
    ],
    cmsSite: [
      'cms-site-list',
      'cms-site-category',
      'cms-site-analytics',
      'cms-site-website',
      'cms-site-sitemap',
      'cms-site-robots',
    ],
    cmsSeo: [
      'cms-seo-overview',
      'cms-seo-question',
      'cms-seo-diagnosis',
      'cms-seo-ai-sampling',
      'cms-seo-keyword',
      'cms-seo-diagnosis-detail',
    ],
    cmsProduct: [
      'cms-business-enterprise',
      'cms-business-brand',
      'cms-business-product',
      'cms-business-product-detail',
    ],
    cmsSettings: [
      'cms-settings-general',
      'cms-settings-theme',
      'cms-settings-forms',
      'cms-settings-form-create',
      'cms-settings-form-detail',
      'cms-settings-submissions',
      'cms-settings-submission-detail',
    ],
    cmsAi: ['cms-ai-topic', 'cms-ai-channel'],
    cmsWorkbench: ['cms-workbench-overview'],
  };

  Object.entries(moduleAliases).forEach(([moduleKey, subPageCodes]) => {
    // 若有模块访问权限，赋予子页面基础访问权限
    if (baseMap[moduleKey]) {
      subPageCodes.forEach((subCode) => {
        if (baseMap[subCode] === undefined) {
          baseMap[subCode] = true;
        }
      });
    }

    // 动作继承兼容
    Object.keys(baseMap).forEach((key) => {
      if (key.startsWith(`${moduleKey}:`)) {
        const action = key.slice(moduleKey.length + 1);
        subPageCodes.forEach((subCode) => {
          const subKey = `${subCode}:${action}`;
          if (baseMap[subKey] === undefined) {
            baseMap[subKey] = baseMap[key];
          }
        });
      }
    });
  });

  return baseMap;
}
